// ===========================================
// RouteNotice: a one-time notice carried in navigation state (#252)
// ===========================================

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Snackbar } from '@mui/material';

/**
 * Shows `location.state.routeNotice` once. The state is cleared from the history entry as soon as
 * it is read, so a refresh or the back button does not bring the notice back.
 */
export default function RouteNotice() {
  const location = useLocation();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<string | null>(null);
  const incoming = (location.state as { routeNotice?: unknown } | null)?.routeNotice;

  useEffect(() => {
    if (typeof incoming !== 'string' || !incoming) return;
    setNotice(incoming);
    // Clear only our key; any other navigation state survives.
    const { routeNotice: _consumed, ...rest } = (location.state ?? {}) as Record<string, unknown>;
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: Object.keys(rest).length ? rest : null,
    });
  }, [incoming, location.pathname, location.search, location.hash, location.state, navigate]);

  return (
    <Snackbar
      open={notice !== null}
      autoHideDuration={8000}
      onClose={(_e, reason) => {
        if (reason !== 'clickaway') setNotice(null);
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="info" onClose={() => setNotice(null)} sx={{ width: '100%' }}>
        {notice}
      </Alert>
    </Snackbar>
  );
}
